'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { JobCreate, PermutationPreview, BatchJobCreate, Series, PermutationList, ImageModel, IMAGE_MODELS } from '@/lib/types';
import api from '@/lib/api';
import UnifiedListModal from './UnifiedListModal';
import TagHighlightTextarea from './TagHighlightTextarea';

interface GenerationFormProps {
  onSubmit: (job: JobCreate) => void;
  onBatchSubmit?: (request: BatchJobCreate) => Promise<void>;
  isLoading?: boolean;
  initialValues?: Partial<JobCreate>;
  series?: Series[];
  selectedSeriesId?: number | null;
  // Controlled prompt mode (optional)
  promptValue?: string;
  onPromptChange?: (prompt: string) => void;
  // Dimension change callback (optional, for preview)
  onDimensionsChange?: (width: number, height: number) => void;
  // Seed change callback (optional, for preview)
  onSeedChange?: (seed: number | null) => void;
  onQualityChange?: (steps: number, cfg: number) => void;
  // Optional id on the underlying <form>, so an external button can submit it via form="..."
  formId?: string;
}

export default function GenerationForm({ onSubmit, onBatchSubmit, isLoading, initialValues, series = [], selectedSeriesId, promptValue, onPromptChange, onDimensionsChange, onSeedChange, onQualityChange, formId }: GenerationFormProps) {
  const [internalPrompt, setInternalPrompt] = useState(initialValues?.prompt || '');

  // Use controlled or uncontrolled prompt
  const isControlled = promptValue !== undefined;
  const prompt = isControlled ? promptValue : internalPrompt;
  const setPrompt = isControlled ? (onPromptChange || (() => {})) : setInternalPrompt;
  const [width, setWidth] = useState(initialValues?.width || 1024);
  const [height, setHeight] = useState(initialValues?.height || 1024);

  // Notify parent of dimension changes
  useEffect(() => {
    onDimensionsChange?.(width, height);
  }, [width, height, onDimensionsChange]);
  const [aspectRatio, setAspectRatio] = useState<number | null>(1); // width/height ratio, null = unlocked
  const [steps, setSteps] = useState(initialValues?.steps || 9);
  const [cfg, setCfg] = useState(initialValues?.cfg || 1.0);
  useEffect(() => {
    onQualityChange?.(steps, cfg);
  }, [steps, cfg, onQualityChange]);
  const [seed, setSeed] = useState<string>(initialValues?.seed?.toString() || '');

  // Notify parent of seed changes
  useEffect(() => {
    const seedNum = seed ? parseInt(seed, 10) : null;
    onSeedChange?.(isNaN(seedNum as number) ? null : seedNum);
  }, [seed, onSeedChange]);
  const [seedCount, setSeedCount] = useState<number>(1);
  const [seriesId, setSeriesId] = useState<number | null>(initialValues?.series_id ?? selectedSeriesId ?? null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [permutationPreview, setPermutationPreview] = useState<PermutationPreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number>(0);

  // Permutation lists for tag highlighting
  const [permutationLists, setPermutationLists] = useState<PermutationList[]>([]);

  // Picker modal state
  const [pickerState, setPickerState] = useState<{
    listId: number;
    listName: string;
    start: number;
    end: number;
  } | null>(null);

  // Update seriesId when selectedSeriesId prop changes (unless initialValues has a series_id)
  useEffect(() => {
    if (!initialValues?.series_id) {
      setSeriesId(selectedSeriesId ?? null);
    }
  }, [selectedSeriesId, initialValues?.series_id]);

  // Fetch permutation lists on mount for tag highlighting
  const fetchPermutationLists = useCallback(async () => {
    try {
      const lists = await api.getPermutationLists();
      setPermutationLists(lists);
    } catch (err) {
      console.error('Failed to fetch permutation lists:', err);
    }
  }, []);

  useEffect(() => {
    fetchPermutationLists();
  }, [fetchPermutationLists]);

  // Debounced permutation preview
  useEffect(() => {
    // Detect {list_name}, {item1|item2}, and [item1|item2] syntax
    const hasNamedLists = /\{[a-zA-Z_][a-zA-Z0-9_]*\}/.test(prompt);
    const hasCurlyInlineLists = /\{[^{}]*\|[^{}]*\}/.test(prompt);  // {item1|item2} with at least one |
    const hasSquareInlineLists = /\[[^\[\]]*\|[^\[\]]*\]/.test(prompt);  // [item1|item2] with at least one |
    const hasTemplateVariables = hasNamedLists || hasCurlyInlineLists || hasSquareInlineLists;

    if (!hasTemplateVariables) {
      setPermutationPreview(null);
      return;
    }

    const timeout = setTimeout(async () => {
      setIsPreviewLoading(true);
      try {
        const preview = await api.previewPermutations(prompt);
        setPermutationPreview(preview);
      } catch (err) {
        console.error('Failed to preview permutations:', err);
        setPermutationPreview(null);
      } finally {
        setIsPreviewLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [prompt]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // If seedCount > 1 and seed is set, create multiple jobs
    if (seedCount > 1 && seed) {
      const baseSeed = parseInt(seed);
      for (let i = 0; i < seedCount; i++) {
        onSubmit({
          prompt,
          width,
          height,
          steps,
          cfg,
          seed: baseSeed + i,
          priority: 0,
          series_id: seriesId,
        });
      }
    } else {
      onSubmit({
        prompt,
        width,
        height,
        steps,
        cfg,
        seed: seed ? parseInt(seed) : null,
        priority: 0,
        series_id: seriesId,
      });
    }
  };

  const handleBatchGenerate = async () => {
    if (!permutationPreview?.is_valid || !onBatchSubmit) return;

    await onBatchSubmit({
      prompt,
      width,
      height,
      steps,
      cfg,
      seed: seed ? parseInt(seed) : null,
      priority: 0,
      series_id: seriesId,
    });
  };

  const handleInsertVariable = (listName: string) => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const variable = `{${listName}}`;
      const newPrompt = prompt.substring(0, start) + variable + prompt.substring(end);
      setPrompt(newPrompt);
      setShowListModal(false);

      // Restore focus and cursor position
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setPrompt(prompt + `{${listName}}`);
      setShowListModal(false);
    }
  };

  // Handle tag click from TagHighlightTextarea
  const handleTagClick = (listId: number, listName: string, start: number, end: number) => {
    setPickerState({ listId, listName, start, end });
  };

  // Handle item selection from picker modal
  const handleItemSelect = (itemValue: string) => {
    if (pickerState) {
      const newPrompt = prompt.substring(0, pickerState.start) + itemValue + prompt.substring(pickerState.end);
      setPrompt(newPrompt);
      setPickerState(null);
    }
  };

  // Refresh lists when modal closes (in case new items were added)
  const handleListModalClose = () => {
    setShowListModal(false);
    fetchPermutationLists();
  };

  // Handle AI prompt enhancement
  const handleEnhancePrompt = async () => {
    if (!prompt.trim() || isEnhancing) return;

    setIsEnhancing(true);
    try {
      const response = await api.enhancePrompt({ prompt });
      setPrompt(response.enhanced_prompt);
    } catch (err) {
      console.error('Failed to enhance prompt:', err);
    } finally {
      setIsEnhancing(false);
    }
  };

  // Handle inserting item value directly from list modal
  const handleInsertItemValue = (value: string) => {
    const pos = cursorPositionRef.current;
    const before = prompt.substring(0, pos);
    const after = prompt.substring(pos);

    // Add space before if needed (not at start and no trailing space/newline)
    const needsSpaceBefore = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n');
    // Add space after if needed (not at end and no leading space/newline)
    const needsSpaceAfter = after.length > 0 && !after.startsWith(' ') && !after.startsWith('\n');

    const spaceBefore = needsSpaceBefore ? ' ' : '';
    const spaceAfter = needsSpaceAfter ? ' ' : '';

    const newPrompt = before + spaceBefore + value + spaceAfter + after;
    setPrompt(newPrompt);
    setShowListModal(false);

    // Restore focus and cursor position after the inserted value
    const newCursorPos = pos + spaceBefore.length + value.length + spaceAfter.length;
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const presets = useMemo(() => [
    { label: '1:1', desc: '1024', width: 1024, height: 1024, ratio: 1 },
    { label: '16:9', desc: '1920x1080', width: 1920, height: 1080, ratio: 16/9 },
    { label: '9:16', desc: '1080x1920', width: 1080, height: 1920, ratio: 9/16 },
    { label: '4:3', desc: '1600x1216', width: 1600, height: 1216, ratio: 1600/1216 },
  ], []);

  // Sync all form values when initialValues changes (e.g., "Use Settings" clicked)
  useEffect(() => {
    if (initialValues) {
      if (initialValues.prompt !== undefined) setPrompt(initialValues.prompt);
      if (initialValues.width !== undefined) setWidth(initialValues.width);
      if (initialValues.height !== undefined) setHeight(initialValues.height);
      if (initialValues.steps !== undefined) setSteps(initialValues.steps);
      if (initialValues.cfg !== undefined) setCfg(initialValues.cfg);
      if (initialValues.seed !== undefined) setSeed(initialValues.seed?.toString() || '');
      if (initialValues.series_id !== undefined) setSeriesId(initialValues.series_id ?? null);

      // Set aspect ratio based on dimensions
      if (initialValues.width && initialValues.height) {
        const ratio = initialValues.width / initialValues.height;
        // Check if it matches a preset ratio (with small tolerance for floating point)
        const matchingPreset = presets.find(p => Math.abs(p.ratio - ratio) < 0.01);
        setAspectRatio(matchingPreset ? matchingPreset.ratio : ratio);
      }
    }
  }, [initialValues, presets]);

  // Round to nearest step (64) and clamp to min/max
  const clampAndRound = useCallback((value: number, min = 512, max = 2048, step = 64) => {
    const rounded = Math.round(value / step) * step;
    return Math.max(min, Math.min(max, rounded));
  }, []);

  // Handle width change - update height if aspect ratio is locked
  const handleWidthChange = useCallback((newWidth: number) => {
    setWidth(newWidth);
    if (aspectRatio !== null) {
      const newHeight = clampAndRound(newWidth / aspectRatio);
      setHeight(newHeight);
    }
  }, [aspectRatio, clampAndRound]);

  // Handle height change - update width if aspect ratio is locked
  const handleHeightChange = useCallback((newHeight: number) => {
    setHeight(newHeight);
    if (aspectRatio !== null) {
      const newWidth = clampAndRound(newHeight * aspectRatio);
      setWidth(newWidth);
    }
  }, [aspectRatio, clampAndRound]);

  // Handle preset selection - set dimensions and lock aspect ratio
  const handlePresetSelect = useCallback((preset: { width: number; height: number; ratio: number }) => {
    setWidth(preset.width);
    setHeight(preset.height);
    setAspectRatio(preset.ratio);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Enter or Cmd+Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (isLoading) return;

        // If batch mode is available and valid, use batch generate
        if (permutationPreview?.is_valid && onBatchSubmit) {
          handleBatchGenerate();
        } else {
          // Trigger form submit
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
        }
      }

      // Alt+1/2/3/4 for aspect ratio presets
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        const presetIndex = parseInt(e.key) - 1;
        if (presetIndex >= 0 && presetIndex < presets.length) {
          e.preventDefault();
          handlePresetSelect(presets[presetIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLoading, permutationPreview, onBatchSubmit, presets, handlePresetSelect]);

  return (
    <>
    <form id={formId} onSubmit={handleSubmit} className="space-y-6">
      {/* Prompt Input with List Button */}
      <div className="space-y-2">
        <div className="relative">
          <TagHighlightTextarea
            ref={textareaRef}
            value={prompt}
            onChange={setPrompt}
            lists={permutationLists}
            onTagClick={handleTagClick}
            className={`input ${promptExpanded ? 'min-h-[80vh] max-h-[80vh]' : 'min-h-[360px] max-h-[360px]'} resize-none pr-36 text-base`}
            placeholder="Describe your vision... Text matching list items will be highlighted - click to change"
            required
          />
          {/* Expand / Collapse prompt height */}
          <button
            type="button"
            onClick={() => setPromptExpanded(p => !p)}
            className="absolute right-24 top-3 p-2 text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)] rounded-lg transition-subtle cursor-pointer z-20"
            title={promptExpanded ? 'Collapse prompt' : 'Expand prompt'}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              {promptExpanded ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M14.25 9h4.5m-4.5 0V4.5m0 4.5l5.25-5.25M9 14.25v4.5m0-4.5H4.5m4.5 0L3.75 19.5M14.25 14.25v4.5m0-4.5h4.5m-4.5 0l5.25 5.25" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9M20.25 20.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              )}
            </svg>
          </button>
          {/* AI Enhance Button */}
          <button
            type="button"
            onClick={handleEnhancePrompt}
            disabled={isEnhancing || !prompt.trim()}
            className="absolute right-12 top-3 p-2 text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)] rounded-lg transition-subtle cursor-pointer z-20 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Enhance prompt with AI"
          >
            {isEnhancing ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            )}
          </button>
          {/* Permutation Lists Button */}
          <button
            type="button"
            onClick={() => {
              // Store cursor position before opening modal
              if (textareaRef.current) {
                cursorPositionRef.current = textareaRef.current.selectionStart;
              } else {
                cursorPositionRef.current = prompt.length;
              }
              setShowListModal(true);
            }}
            className="absolute right-3 top-3 p-2 text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)] rounded-lg transition-subtle cursor-pointer z-20"
            title="Manage Permutation Lists"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
            </svg>
          </button>
        </div>

        {/* Permutation Preview Banner */}
        {isPreviewLoading && (
          <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] flex items-center gap-2 text-sm text-[var(--text-muted)]">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Analyzing permutations...
          </div>
        )}

        {!isPreviewLoading && permutationPreview && (
          <div className={`p-4 rounded-lg border ${
            permutationPreview.is_valid
              ? 'bg-[rgba(45,212,191,0.1)] border-[var(--success)]'
              : 'bg-[rgba(251,191,36,0.1)] border-[#fbbf24]'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">
                  {permutationPreview.is_valid ? (
                    <span className="text-[var(--success)]">
                      {permutationPreview.total_permutations} permutation{permutationPreview.total_permutations !== 1 ? 's' : ''} detected
                    </span>
                  ) : (
                    <span className="text-[#fbbf24]">
                      Missing: {permutationPreview.missing_lists.join(', ')}
                    </span>
                  )}
                </div>
                {permutationPreview.is_valid && permutationPreview.sample_permutations.length > 0 && (
                  <p className="text-xs text-[var(--text-muted)] mt-1 truncate">
                    Sample: &ldquo;{permutationPreview.sample_permutations[0]}&rdquo;
                  </p>
                )}
                {permutationPreview.detected_lists.length > 0 && (
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Lists: {permutationPreview.detected_lists.map(l => `{${l}}`).join(', ')}
                  </p>
                )}
              </div>
              {permutationPreview.is_valid && onBatchSubmit && (
                <button
                  type="button"
                  onClick={handleBatchGenerate}
                  disabled={isLoading}
                  className="btn btn-primary flex-shrink-0"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Creating...
                    </span>
                  ) : (
                    `Generate ${permutationPreview.total_permutations} Images`
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Series Selector */}
      {series.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="text-sm text-[var(--text-muted)] whitespace-nowrap">Series</label>
          <select
            value={seriesId ?? ''}
            onChange={(e) => setSeriesId(e.target.value ? parseInt(e.target.value) : null)}
            className="input text-sm py-2 flex-1"
          >
            <option value="">No Series</option>
            {series.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Aspect Ratio Presets */}
      <div className="flex gap-2">
        {presets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => handlePresetSelect(preset)}
            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-subtle cursor-pointer ${
              aspectRatio === preset.ratio
                ? 'bg-[var(--accent)] text-black'
                : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]'
            }`}
          >
            <span className="block">{preset.label}</span>
          </button>
        ))}
      </div>

      {/* Advanced Toggle */}
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-subtle cursor-pointer"
      >
        <svg
          className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
        Advanced
      </button>

      {/* Advanced Settings */}
      {showAdvanced && (
        <div className="grid grid-cols-2 gap-6 p-5 bg-[var(--bg-surface)] rounded-xl border border-[var(--border-subtle)] animate-fadeIn">
          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Width</span>
              <span className="text-[var(--text-primary)] font-medium">{width}</span>
            </label>
            <input
              type="range"
              value={width}
              onChange={(e) => handleWidthChange(parseInt(e.target.value))}
              min={512}
              max={2048}
              step={64}
              className="w-full accent-[var(--accent)] cursor-pointer"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Height</span>
              <span className="text-[var(--text-primary)] font-medium">{height}</span>
            </label>
            <input
              type="range"
              value={height}
              onChange={(e) => handleHeightChange(parseInt(e.target.value))}
              min={512}
              max={2048}
              step={64}
              className="w-full accent-[var(--accent)] cursor-pointer"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Steps</span>
              <span className="text-[var(--text-primary)] font-medium">{steps}</span>
            </label>
            <input
              type="range"
              value={steps}
              onChange={(e) => setSteps(parseInt(e.target.value))}
              min={1}
              max={50}
              className="w-full accent-[var(--accent)] cursor-pointer"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">CFG</span>
              <span className="text-[var(--text-primary)] font-medium">{cfg.toFixed(1)}</span>
            </label>
            <input
              type="range"
              value={cfg}
              onChange={(e) => setCfg(parseFloat(e.target.value))}
              min={0}
              max={20}
              step={0.1}
              className="w-full accent-[var(--accent)] cursor-pointer"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm text-[var(--text-muted)]">Seed</label>
            <input
              type="number"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              className="input"
              placeholder="Random"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm text-[var(--text-muted)]">Count</label>
            <input
              type="number"
              value={seedCount}
              onChange={(e) => setSeedCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              className="input"
              min={1}
              max={100}
              placeholder="1"
            />
            {seedCount > 1 && seed && (
              <p className="text-xs text-[var(--text-muted)]">
                Seeds {seed} to {parseInt(seed) + seedCount - 1}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Submit Button - show only if no valid permutations or no batch handler */}
      {(!permutationPreview?.is_valid || !onBatchSubmit) && (
        <button
          type="submit"
          disabled={isLoading || !prompt.trim()}
          className="btn btn-primary w-full h-12 text-base font-semibold"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Generating...
            </span>
          ) : seedCount > 1 && seed ? (
            `Generate ${seedCount} Images`
          ) : (
            'Generate'
          )}
        </button>
      )}

    </form>

    {/* Unified List Modal - Manager Mode */}
    {showListModal && (
      <UnifiedListModal
        initialMode="manager"
        onClose={handleListModalClose}
        onInsertVariable={handleInsertVariable}
        onSelect={handleInsertItemValue}
      />
    )}

    {/* Unified List Modal - Picker Mode */}
    {pickerState && (
      <UnifiedListModal
        initialMode="picker"
        initialListId={pickerState.listId}
        onSelect={handleItemSelect}
        onClose={() => setPickerState(null)}
      />
    )}
  </>
  );
}
